/** Phase 4: iXBRL, taxonomy version control, Companies House filing and the Corporation Tax handoff. */

/** ap.DocumentVersion.Status */
export enum DocumentStatusId {
  Requested = 0,
  Generating = 1,
  Generated = 2,
  Approved = 3,
  Final = 4,
  Failed = 5,
}

/** ap.FilingAttempt.Status */
export enum FilingStatusId {
  Prepared = 0,
  Submitted = 1,
  Pending = 2,
  Accepted = 3,
  Rejected = 4,
  Failed = 5,
}

/** ap.IxbrlTagException.Status */
export enum TagExceptionStatusId {
  Open = 0,
  Resolved = 1,
  AcceptedUntagged = 2,
}

/** ap.TaxHandoff.Status */
export enum TaxHandoffStatusId {
  Prepared = 0,
  Sent = 1,
  Accepted = 2,
  Rejected = 3,
  Failed = 4,
}

// ---- Pickers ----

export interface DocumentVersionOption {
  documentVersionId: string;
  accountingPeriodId: string;
  versionNo: number;
  type: number;
  typeName: string;
  status: DocumentStatusId;
  statusName: string;
  contentHash: string | null;
  taxonomyVersionId: string | null;
  dataVersion: number;
  generatedAtUtc: string | null;
  label: string;
  /** MEMBERS (Full), ABRIDGED or FILLETED; absent from an API that predates output profiles. */
  outputProfile?: string | null;
}

export interface PeriodOption {
  accountingPeriodId: string;
  startDate: string;
  endDate: string;
  status: number;
  statusName: string;
  label: string;
  documents: DocumentVersionOption[];
}

// ---- Taxonomy ----

export interface TaxonomyVersionRow {
  taxonomyVersionId: string;
  regulator: string;
  taxonomyName: string;
  taxonomyVersion: string;
  acceptedFrom: string;
  acceptedTo: string | null;
  status: number;
  statusName: string;
  tagMappingCount: number;
}

// ---- iXBRL ----

export interface IxbrlMessageRow {
  code: string;
  severity: string;
  message: string;
  conceptQName: string | null;
  contextRef: string | null;
}

export interface IxbrlJobRow {
  ixbrlJobId: string;
  accountingPeriodId: string;
  documentVersionId: string;
  documentVersionNo: number;
  documentStatus: number;
  taxonomyVersionId: string;
  taxonomyName: string | null;
  taxonomyVersion: string | null;
  status: number;
  statusName: string;
  isValid: boolean;
  isValidateOnly: boolean;
  factCount: number;
  exceptionCount: number;
  dataVersion: number;
  contentHash: string | null;
  storageRef: string | null;
  failureReason: string | null;
  requestedAtUtc: string;
  completedAtUtc: string | null;
  isStale: boolean;
  messages: IxbrlMessageRow[];
}

export interface IxbrlFactRow {
  ixbrlFactIndexId: number;
  ixbrlJobId: string;
  documentVersionId: string;
  conceptQName: string;
  contextRef: string;
  unitRef: string | null;
  decimals: string | null;
  renderedValue: string;
  numericValue: number | null;
  sourceType: string;
  sourceCode: string;
  sourceCaption: string | null;
  dimensions: Record<string, string>;
  isCurrentPeriod: boolean;
  taggingMode: string;
  taxonomyVersionId: string;
  taxonomyName: string | null;
  taxonomyVersion: string | null;
}

export interface IxbrlInspectorResult {
  accountingPeriodId: string;
  documentVersionId: string;
  job: IxbrlJobRow | null;
  openExceptionCount: number;
  blockingExceptionCount: number;
  facts: IxbrlFactRow[];
}

export interface IxbrlTagExceptionRow {
  ixbrlTagExceptionId: string;
  accountingPeriodId: string;
  documentVersionId: string;
  ixbrlJobId: string | null;
  taxonomyVersionId: string;
  taxonomyName: string | null;
  taxonomyVersion: string | null;
  exceptionCode: string;
  sourceType: string;
  sourceCode: string;
  sourceCaption: string | null;
  amount: number | null;
  severity: number;
  severityName: string;
  isBlocking: boolean;
  reason: string;
  status: TagExceptionStatusId;
  statusName: string;
  resolvedConceptQName: string | null;
  resolutionNote: string | null;
  raisedAtUtc: string;
  resolvedAtUtc: string | null;
  resolvedByName: string | null;
}

export interface TaxonomyConceptOption {
  conceptQName: string;
  sourceType: string;
  sourceCode: string;
  contextRule: string | null;
  unitRef: string | null;
}

export interface GenerateIxbrlPayload {
  accountingPeriodId: string;
  documentVersionId: string;
  taxonomyVersionId: string | null;
  validateOnly: boolean;
}

export interface ResolveTagExceptionPayload {
  ixbrlTagExceptionId: string;
  /** 1 tag with a concept, 2 accept the figure untagged. */
  action: number;
  conceptQName: string | null;
  note: string | null;
}

// ---- Filing ----

export interface FilingCheckRow {
  srNo: number;
  code: string;
  check: string;
  severity: string;
  isBlocking: boolean;
  message: string;
  route: string | null;
}

export interface FilingPreFlightReport {
  accountingPeriodId: string;
  documentVersionId: string;
  documentVersionNo: number;
  documentStatusName: string;
  outputProfile?: string | null;
  canSubmit: boolean;
  blockingCount: number;
  warningCount: number;
  companyNumber: string | null;
  hasAuthenticationCode: boolean;
  packageContentHash: string | null;
  packageLength: number;
  factCount: number;
  openBlockingExceptionCount: number;
  taxonomyVersionId: string | null;
  taxonomyName: string | null;
  taxonomyVersion: string | null;
  hasFilingPackage: boolean;
  checks: FilingCheckRow[];
}

export interface FilingPackageRow {
  filingPackageId: string;
  accountingPeriodId: string;
  documentVersionId: string;
  taxonomyVersionId: string | null;
  regulator: string;
  companyNumber: string;
  contentHash: string;
  contentLength: number;
  factCount: number;
  createdAtUtc: string;
}

export interface RegulatorResponseRow {
  regulatorResponseId: number;
  filingAttemptId: string;
  responseType: string;
  status: FilingStatusId;
  statusName: string;
  code: string | null;
  severity: number;
  severityName: string;
  message: string;
  isSandbox: boolean;
  receivedAtUtc: string;
}

export interface FilingTimelineRow {
  filingAttemptId: string;
  occurredAtUtc: string;
  status: string;
  description: string;
}

export interface FilingAttemptHistoryRow {
  filingAttemptId: string;
  accountingPeriodId: string;
  documentVersionId: string;
  documentVersionNo: number;
  outputProfile?: string | null;
  regulator: string;
  submissionId: string | null;
  status: FilingStatusId;
  statusName: string;
  isSandbox: boolean;
  responseRef: string | null;
  documentContentHash: string | null;
  packageContentHash: string | null;
  packageLength: number;
  preparedAtUtc: string;
  submittedAtUtc: string | null;
  respondedAtUtc: string | null;
  preparedByName: string | null;
  timeline: FilingTimelineRow[];
  responses: RegulatorResponseRow[];
}

export interface FilingHistoryResult {
  accountingPeriodId: string;
  attempts: FilingAttemptHistoryRow[];
}

// ---- Corporation Tax handoff ----

export interface TaxHandoffPreview {
  accountingPeriodId: string;
  documentVersionId: string;
  documentVersionNo: number;
  documentStatusName: string;
  canHandOff: boolean;
  periodStart: string;
  periodEnd: string;
  companyName: string;
  companyNumber: string | null;
  utr: string | null;
  statutoryProfit: number;
  documentContentHash: string | null;
  ixbrlContentHash: string | null;
  hasValidIxbrl: boolean;
  atbLineCount: number;
  isConfigured: boolean;
  checks: FilingCheckRow[];
  /** CORPORATION_TAX for a limited company; SELF_ASSESSMENT for a sole trader, partnership or LLP. */
  taxTarget?: 'CORPORATION_TAX' | 'SELF_ASSESSMENT';
  /** The return(s) a Self Assessment handoff feeds, e.g. "SA103 / SA105"; empty for Corporation Tax. */
  taxReturns?: string;
  clientType?: number;
}

export interface TaxHandoffRow {
  taxHandoffId: string;
  accountingPeriodId: string;
  documentVersionId: string;
  documentVersionNo: number;
  clientId: string;
  taxPeriodStart: string;
  taxPeriodEnd: string;
  status: TaxHandoffStatusId;
  statusName: string;
  statutoryProfit: number;
  documentContentHash: string | null;
  ixbrlContentHash: string | null;
  ctReference: string | null;
  createdAtUtc: string;
  completedAtUtc: string | null;
  createdByName: string | null;
  messages: IxbrlMessageRow[];
}

export interface CreateTaxHandoffPayload {
  accountingPeriodId: string;
  documentVersionId: string;
  taxPeriodStart: string;
  taxPeriodEnd: string;
}
